/**
 * INV-F2 · Uma única definição de "encerrar sessão".
 *
 * Este módulo é a ÚNICA fonte de verdade sobre "o que conta como sessão" no
 * cliente. Todo ponto de logout — `authService.logout()`, o interceptor de 401
 * em `services/api.js`, o botão "Sair" da `Agenda` e o catch do bootstrap da
 * `AuthPage` — chama `clearSession()`. Nenhum outro ponto do `src/` remove
 * chave de sessão do `localStorage` por conta própria.
 *
 * O bug que originou a regra não foi "token não removido": foi DIVERGÊNCIA.
 * Cada ponto limpava um subconjunto diferente, então quem saía por um caminho
 * deixava resíduo que outro caminho teria limpado. Logout que não desloga por
 * completo é bug de segurança, não de UX.
 *
 * ── Por que mora em `src/auth/` e não em `src/services/` ──────────────────
 * `services/` é a camada de clientes HTTP (Axios). Sessão é estado de
 * autenticação do cliente, e é em `src/auth/` que ela já morava: o
 * `AuthContext` guarda o usuário e definia aqui o cache de segmento do tenant.
 * Colocar em `services/` também convidaria a um ciclo de import: `api.js`
 * precisa de `clearSession` no interceptor de 401, e `authService.js` importa
 * `api.js`. Este módulo, por isso, **não importa nada** — nem `api.js`, nem
 * `authService.js` — e pode ser importado de qualquer lugar sem ciclo.
 */

/**
 * Cache do `profile.tenant.segment`, usado por `Agenda.js` e `buildMenu.js`
 * para filtrar o menu lateral antes do `businessData` chegar. Exportado daqui
 * para que o literal `"nx_tenant_segment"` exista em UM lugar só.
 */
export const TENANT_SEGMENT_STORAGE_KEY = "nx_tenant_segment";

/**
 * A lista canônica. Decidida em 14/08/2026; qualquer chave nova de sessão
 * entra AQUI, não num `removeItem` solto.
 *
 * Revisão de 18/08/2026 — duas chaves saíram por serem código morto:
 *
 * - `tenantId` era escrita no signup (`AuthPage.js`) e NUNCA lida: quem precisa
 *   do tenant lê de dentro de `user` (`JSON.parse(localStorage.user).tenantId`,
 *   ver `ChatWidget.js`). O `AuthSuccess.js` lê um `tenantId` homônimo, mas do
 *   FRAGMENTO da URL — nada a ver com esta chave. O `setItem` foi removido junto;
 *   limpar uma chave que ninguém mais escreve seria manutenção de fantasma.
 *
 * - `app_user` nunca foi escrita em `src/` em nenhuma versão viva do código; o
 *   `removeItem` era herança de um `Agenda.js` já reescrito. Sai pelo mesmo
 *   motivo. ⚠️ Trade-off assumido: se algum browser antigo ainda guardar um
 *   `app_user`, ele deixa de ser apagado no logout. É dado inerte — nenhum ponto
 *   do `src/` o lê — mas a limpeza defensiva se perde.
 *
 * NÃO entram nesta lista, de propósito:
 * - `pendingSubscription` — fluxo de compra; sobrevive ao logout porque o
 *   usuário pode escolher o plano deslogado e autenticar em seguida.
 * - `meta_state_*` — nonce CSRF do OAuth da Meta; tem ciclo próprio em
 *   `src/utils/stateManager.js` (criado e consumido na mesma ida-e-volta).
 */
export const SESSION_STORAGE_KEYS = [
  "token",
  "user",
  "customer",
  TENANT_SEGMENT_STORAGE_KEY,
];

/**
 * Remove TODAS as chaves de sessão do `localStorage`.
 *
 * try/catch defensivo, no mesmo padrão de `persistTenantSegment` no
 * `AuthContext.js`: localStorage pode estar indisponível em modo privado de
 * browsers antigos ou com quota cheia. Falha silenciosa é preferível a
 * quebrar o logout — um erro propagado aqui interromperia o redirect e
 * deixaria o usuário preso numa tela autenticada.
 *
 * O try é POR CHAVE, não em volta do laço: se a primeira chave lançar, as
 * demais ainda são tentadas. Um catch único abortaria a limpeza no meio e
 * deixaria exatamente o resíduo de sessão que este invariante existe para
 * eliminar.
 */
export function clearSession() {
  SESSION_STORAGE_KEYS.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // localStorage indisponível para esta chave — ignorar e seguir.
    }
  });
}
